import { Footer } from "@/components/footer"
import { TrendingUp, Users, Shield, DollarSign, BarChart3, Zap } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SEO } from "@/components/SEO"

export default function ProvidersPage() {
  const benefits = [
    {
      icon: TrendingUp,
      title: "Grow Your Business",
      description: "Reach thousands of potential customers actively looking for your services",
    },
    {
      icon: Users,
      title: "Direct Customer Connection",
      description: "Negotiate directly with customers and build lasting relationships",
    },
    {
      icon: Shield,
      title: "Verified Platform",
      description: "Secure transactions and verified customer reviews build trust",
    },
    {
      icon: DollarSign,
      title: "Flexible Pricing",
      description: "Set your own prices and negotiate deals that work for your business",
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Track bookings, revenue, and customer feedback in real-time",
    },
    {
      icon: Zap,
      title: "Instant Notifications",
      description: "Never miss a booking request with real-time alerts",
    },
  ]

  const steps = [
    {
      number: "01",
      title: "Sign Up",
      description: "Create your provider account in minutes",
    },
    {
      number: "02",
      title: "Complete Profile",
      description: "Add your services, pricing, and business details",
    },
    {
      number: "03",
      title: "Get Verified",
      description: "We verify your business to build customer trust",
    },
    {
      number: "04",
      title: "Start Receiving Bookings",
      description: "Accept requests and grow your customer base",
    },
  ]

  return (
    <>
      <SEO
        title="For Service Providers - StayApp | Grow Your Business with StayApp"
        description="Join StayApp as a service provider. Reach thousands of customers, set flexible pricing, and grow your accommodation, dining, entertainment, wellness, or transport business."
        keywords="service provider, business partner, join StayApp, provider registration, service marketplace"
        ogImage="/logo.png"
        ogUrl="https://stayapp.com/providers"
        canonical="https://stayapp.com/providers"
      />
      <main className="pt-28 md:pt-36 lg:pt-40">
      {/* Hero Section */}
      <section className="bg-black text-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-[#75055B] px-2">
              Grow Your Business with StayApp
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 px-4">
              Join thousands of service providers reaching customers across accommodation, dining, entertainment,
              wellness, and transport
            </p>
            <Button
              size="lg"
              className="bg-[#75055B] text-white hover:bg-[#DB4789] hover:shadow-lg hover:shadow-[#DB4789]/30 px-6 sm:px-8 w-full sm:w-auto"
            >
              Become a Provider
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mb-3 sm:mb-4 px-2">
              Why Choose StayApp?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Powerful tools and features designed to help your business thrive
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-[#DB4789] hover:shadow-lg transition-all"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#75055B] flex items-center justify-center mb-3 sm:mb-4">
                  <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-black mb-1 sm:mb-2">{benefit.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mb-3 sm:mb-4 px-2">
              Getting Started is Easy
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="text-center px-2">
                <div className="text-5xl sm:text-6xl font-bold text-[#75055B] mb-3 sm:mb-4">{step.number}</div>
                <h3 className="text-lg sm:text-xl font-bold text-black mb-1 sm:mb-2">{step.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-[#75055B]">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">Ready to Join?</h2>
          <p className="text-base sm:text-lg text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Start receiving booking requests today and grow your business with StayApp
          </p>
          <Button
            size="lg"
            className="bg-white text-black hover:bg-gray-100 shadow-lg px-6 sm:px-8 w-full sm:w-auto max-w-xs"
          >
            Register as Provider
          </Button>
        </div>
      </section>

      <Footer />
    </main>
    </>
  )
}

